import React, { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../../components/ui/dialog";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogAction, AlertDialogCancel,
} from "../../components/ui/alert-dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Copy, Search, ImagePlus } from "lucide-react";
import ImageUpload, { uploadImage } from "../../components/admin/ImageUpload";

function HtmlField({ field, value, onChange }) {
  const ref = React.useRef(null);
  const [busy, setBusy] = React.useState(false);
  const insertImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const url = await uploadImage(file);
      onChange(`${value || ""}\n<img src="${url}" alt="" />\n`);
      toast.success("Image inserted");
    } catch (err) { toast.error("Upload failed"); } finally { setBusy(false); if (ref.current) ref.current.value = ""; }
  };
  return (
    <div>
      <Textarea rows={field.rows || 8} value={value || ""} onChange={(e) => onChange(e.target.value)} className="bg-ink border-line font-mono text-xs" data-testid={`field-${field.name}`} />
      <input ref={ref} type="file" accept="image/*" className="hidden" onChange={insertImage} data-testid={`field-${field.name}-img-input`} />
      <button type="button" onClick={() => ref.current?.click()} disabled={busy}
        className="mt-2 inline-flex items-center gap-1.5 text-xs text-ember hover:underline" data-testid={`field-${field.name}-insert-image`}>
        <ImagePlus className="w-3.5 h-3.5" /> {busy ? "Uploading…" : "Upload & insert image"}
      </button>
    </div>
  );
}

function FieldInput({ field, value, onChange, relations }) {
  const common = "bg-ink border-line";
  if (field.type === "image")
    return <ImageUpload value={value} onChange={onChange} testid={`field-${field.name}`} />;
  if (field.type === "html")
    return <HtmlField field={field} value={value} onChange={onChange} />;
  if (field.type === "textarea")
    return <Textarea rows={field.rows || 4} value={value || ""} onChange={(e) => onChange(e.target.value)} className={common} data-testid={`field-${field.name}`} />;
  if (field.type === "switch")
    return <div className="flex items-center h-10"><Switch checked={!!value} onCheckedChange={onChange} data-testid={`field-${field.name}`} /></div>;
  if (field.type === "number")
    return <Input type="number" value={value ?? ""} onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))} className={common} />;
  if (field.type === "select") {
    const opts = field.options || (relations[field.relation] || []).map((r) => ({ value: r[field.optValue || "id"], label: r[field.optLabel || "name"] }));
    return (
      <select value={value ?? ""} onChange={(e) => onChange(e.target.value)} className={`${common} w-full h-10 rounded-md px-3 text-sm`} data-testid={`field-${field.name}`}>
        <option value="">— Select —</option>
        {opts.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    );
  }
  if (field.type === "tags")
    return <Input value={Array.isArray(value) ? value.join(", ") : value || ""} onChange={(e) => onChange(e.target.value.split(",").map((s) => s.trim()).filter(Boolean))} placeholder="comma, separated" className={common} />;
  return <Input value={value ?? ""} onChange={(e) => onChange(e.target.value)} className={common} data-testid={`field-${field.name}`} />;
}

export default function ResourceManager({ title, subtitle, endpoint, columns, fields, relations: relConfig = [], canDuplicate, defaults = {}, testid }) {
  const [rows, setRows] = useState([]);
  const [relations, setRelations] = useState({});
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [delId, setDelId] = useState(null);

  const load = useCallback(() => { api.get(endpoint).then((r) => setRows(r.data)).catch(() => {}); }, [endpoint]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    relConfig.forEach((rc) => api.get(rc.url).then((r) => setRelations((p) => ({ ...p, [rc.key]: r.data }))).catch(() => {}));
  }, []);

  const openNew = () => { setEditing(null); setForm({ ...defaults }); setOpen(true); };
  const openEdit = (row) => { setEditing(row); setForm({ ...row }); setOpen(true); };

  const save = async () => {
    setBusy(true);
    try {
      if (editing) await api.put(`${endpoint}/${editing.id}`, form);
      else await api.post(endpoint, form);
      toast.success(editing ? "Updated" : "Created");
      setOpen(false); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setBusy(false); }
  };
  const doDelete = async () => {
    try { await api.delete(`${endpoint}/${delId}`); toast.success("Deleted"); load(); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setDelId(null); }
  };
  const duplicate = async (row) => {
    try { await api.post(`${endpoint}/${row.id}/duplicate`); toast.success("Duplicated"); load(); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const togglePublish = async (row) => {
    try {
      await api.put(`${endpoint}/${row.id}`, { published: !row.published });
      toast.success(row.published ? "Unpublished" : "Published");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const filtered = rows.filter((r) => !q || JSON.stringify(r).toLowerCase().includes(q.toLowerCase()));

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div><h1 className="font-heading text-2xl font-bold">{title}</h1>{subtitle && <p className="text-sm text-content-secondary mt-1">{subtitle}</p>}</div>
        <Button className="btn-ember rounded-full" onClick={openNew} data-testid={`${testid}-add`}><Plus className="w-4 h-4 mr-1" /> New</Button>
      </div>

      <div className="relative max-w-xs mb-4">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-content-muted" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" className="pl-9 bg-card border-line" data-testid={`${testid}-search`} />
      </div>

      <div className="card-surface overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-line text-left text-content-muted">
            {columns.map((c) => <th key={c.key} className="px-4 py-3 font-medium whitespace-nowrap">{c.label}</th>)}
            <th className="px-4 py-3" />
          </tr></thead>
          <tbody>
            {filtered.map((row) => (
              <tr key={row.id} className="border-b border-line/50 hover:bg-card-hover/50" data-testid={`${testid}-row-${row.id}`}>
                {columns.map((c) => <td key={c.key} className="px-4 py-3 align-top">{c.key === "published" ? (
                  <div className="flex items-center gap-2">
                    <Switch checked={!!row.published} onCheckedChange={() => togglePublish(row)} data-testid={`${testid}-publish-toggle-${row.id}`} aria-label="Toggle published" />
                    {c.render ? c.render(row) : <Badge on={row.published} />}
                  </div>
                ) : c.render ? c.render(row) : String(row[c.key] ?? "—")}</td>)}
                <td className="px-4 py-3">
                  <div className="flex gap-1 justify-end">
                    {canDuplicate && <button onClick={() => duplicate(row)} className="p-1.5 hover:text-ember" title="Duplicate" data-testid={`${testid}-dup-${row.id}`}><Copy className="w-4 h-4" /></button>}
                    <button onClick={() => openEdit(row)} className="p-1.5 hover:text-ember" title="Edit" data-testid={`${testid}-edit-${row.id}`}><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => setDelId(row.id)} className="p-1.5 hover:text-red-400" title="Delete" data-testid={`${testid}-del-${row.id}`}><Trash2 className="w-4 h-4" /></button>
                  </div>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={columns.length + 1} className="px-4 py-10 text-center text-content-muted">No records.</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-card border-line max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Edit" : "New"} {title.replace(/s$/, "")}</DialogTitle></DialogHeader>
          <div className="grid sm:grid-cols-2 gap-4">
            {fields.map((f) => (
              <div key={f.name} className={f.full || f.type === "textarea" || f.type === "html" ? "sm:col-span-2" : ""}>
                <label className="text-sm text-content-secondary">{f.label}</label>
                <div className="mt-1"><FieldInput field={f} value={form[f.name]} onChange={(v) => setForm((p) => ({ ...p, [f.name]: v }))} relations={relations} /></div>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" className="border-line" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="btn-ember" onClick={save} disabled={busy} data-testid={`${testid}-save`}>{busy ? "Saving…" : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!delId} onOpenChange={() => setDelId(null)}>
        <AlertDialogContent className="bg-card border-line">
          <AlertDialogHeader><AlertDialogTitle>Delete this record?</AlertDialogTitle><AlertDialogDescription>This action cannot be undone.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel className="border-line">Cancel</AlertDialogCancel><AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={doDelete} data-testid={`${testid}-confirm-del`}>Delete</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export const Badge = ({ on, onText = "Published", offText = "Draft" }) => (
  <span className={`text-[11px] px-2 py-0.5 rounded-full ${on ? "bg-emerald-500/15 text-emerald-400" : "bg-card-hover text-content-muted"}`}>{on ? onText : offText}</span>
);
